/**
 * The itinerary editor's header block: archive / abandon actions, the archived
 * banner, the inline title rename (#249), the inline description editor, the
 * auto-save status pill (D8) and the audience buttons.
 *
 * Lifted out of `ItineraryEditor` (#314) following the `StopDetailsModal`
 * precedent (#245). It owns only its two local drafts — every save is handed
 * back to the editor, which routes it through the serial mutation queue.
 *
 * Form convention (repo, non-negotiable): action buttons TOP RIGHT, <div> not
 * <form>, onClick not onSubmit.
 */

import { useState } from 'react';
import { AutoSaveStatusPill } from './AutoSaveStatusPill';
import type { ItineraryAudience } from './useItineraryPlanOps';
import type {
  AutoSaveStatus,
  MutationQueueState,
} from './useItineraryMutationQueue';
import { Heading1, BodyText, Caption } from '../ui/Typography';
import { Button } from '../ui/Button';

const AUDIENCE_LABEL: Record<ItineraryAudience, string> = {
  private: 'Private',
  unlisted: 'Unlisted',
  public: 'Public',
};

interface ItineraryEditorHeaderProps {
  title: string;
  description?: string;
  audience: ItineraryAudience;
  archived: boolean;
  status: AutoSaveStatus;
  failure: MutationQueueState['failure'];
  onRetry: () => void;
  onDismiss: () => void;
  onSaveTitle: (value: string) => void;
  onSaveDescription: (value: string) => void;
  onFlipAudience: (audience: ItineraryAudience) => void;
  onToggleArchive: () => void;
  onAbandon: () => void;
}

export const ItineraryEditorHeader = ({
  title,
  description,
  audience,
  archived,
  status,
  failure,
  onRetry,
  onDismiss,
  onSaveTitle,
  onSaveDescription,
  onFlipAudience,
  onToggleArchive,
  onAbandon,
}: ItineraryEditorHeaderProps) => {
  const [descDraft, setDescDraft] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState<string | null>(null);

  return (
    <div className="border-b border-[#DBD4C9] pb-4">
      {/* Action buttons — TOP RIGHT (repo form rule). */}
      <div className="mb-4 flex flex-wrap justify-end gap-3">
        <Button variant="outline" onClick={onToggleArchive}>
          {archived ? 'Unarchive' : 'Archive'}
        </Button>
        <Button variant="danger" onClick={onAbandon}>
          Abandon
        </Button>
      </div>

      {archived && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <BodyText className="text-gray-800">
            This itinerary is archived — it&apos;s hidden from browse but your
            copy is intact.
          </BodyText>
        </div>
      )}

      {/* Inline title rename (#249) — mirrors the day "Rename" affordance:
          the heading stays a real <h1> (a button may only contain phrasing
          content, and burying the h1 inside one costs the page its heading
          landmark) with Rename as a sibling action. */}
      {titleDraft === null ? (
        <div className="flex items-baseline gap-3">
          <Heading1>{title}</Heading1>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setTitleDraft(title)}
            aria-label="Rename itinerary"
          >
            Rename
          </Button>
        </div>
      ) : (
        <div className="max-w-3xl space-y-2">
          <input
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setTitleDraft(null);
              if (e.key === 'Enter' && titleDraft.trim()) {
                onSaveTitle(titleDraft);
                setTitleDraft(null);
              }
            }}
            maxLength={200}
            autoFocus
            aria-label="Itinerary title"
            className="w-full rounded border border-stone-300 px-3 py-2 font-serif text-2xl font-normal text-gray-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <div className="flex items-center justify-between">
            <Caption className="text-stone-500">
              {titleDraft.trim()
                ? `${titleDraft.length} / 200`
                : 'A title is required.'}
            </Caption>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTitleDraft(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!titleDraft.trim()}
                onClick={() => {
                  onSaveTitle(titleDraft);
                  setTitleDraft(null);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-2">
        {descDraft === null ? (
          description ? (
            <button
              type="button"
              onClick={() => setDescDraft(description ?? '')}
              className="text-left text-stone-700 hover:text-stone-900 hover:bg-stone-50 rounded -mx-1 px-1 py-0.5 transition-colors w-full max-w-3xl"
              aria-label="Edit description"
            >
              <BodyText className="whitespace-pre-wrap">{description}</BodyText>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setDescDraft('')}
              className="text-sm italic text-stone-500 hover:text-primary-700"
            >
              + Add a description
            </button>
          )
        ) : (
          <div className="space-y-2 max-w-3xl">
            <textarea
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setDescDraft(null);
              }}
              placeholder="A short description for this itinerary…"
              maxLength={2000}
              rows={3}
              autoFocus
              className="w-full rounded border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
            <div className="flex items-center justify-between">
              <Caption className="text-stone-500">
                {descDraft.length} / 2000
              </Caption>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDescDraft(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    onSaveDescription(descDraft);
                    setDescDraft(null);
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <AutoSaveStatusPill
          status={status}
          failure={failure}
          onRetry={onRetry}
          onDismiss={onDismiss}
        />
        <div className="flex items-center gap-2">
          <Caption className="text-gray-600">Audience</Caption>
          {(['private', 'unlisted', 'public'] as const).map((a) => (
            <Button
              key={a}
              size="sm"
              variant={audience === a ? 'primary' : 'outline'}
              aria-pressed={audience === a}
              onClick={() => onFlipAudience(a)}
            >
              {AUDIENCE_LABEL[a]}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
